
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookingDetails, ServiceFrequency, PlantRange, ServiceType } from '@/types';
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Droplets, Sun, Trees, Calendar, Clock, MapPin, DollarSign } from "lucide-react";

interface BookingSummaryProps {
  bookingDetails: BookingDetails;
  selectedDate?: Date;
  selectedTimeSlot?: string;
  onConfirm: () => void;
  isLoading?: boolean;
}

const BookingSummary: React.FC<BookingSummaryProps> = ({
  bookingDetails,
  selectedDate,
  selectedTimeSlot,
  onConfirm,
  isLoading = false
}) => {
  if (!selectedDate || !selectedTimeSlot) return null;

  const getServiceIcon = () => {
    switch (bookingDetails.serviceType) {
      case ServiceType.Indoor:
        return <Droplets className="h-4 w-4 text-gray-600" />;
      case ServiceType.Outdoor:
        return <Sun className="h-4 w-4 text-gray-600" />;
      case ServiceType.Combined:
        return <Trees className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getTimeSlotDisplay = () => {
    switch (selectedTimeSlot) {
      case "earlyMorning":
        return "6:00am - 10:00am";
      case "evening":
        return "6:00pm - 8:00pm";
      default:
        return selectedTimeSlot;
    }
  };

  const calculateTotalWithAddOns = () => {
    let total = bookingDetails.price;
    if (bookingDetails.addOns && bookingDetails.addOns.length > 0) {
      bookingDetails.addOns.forEach(addon => {
        total += addon.price;
      });
    }
    return total;
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-black mb-6">Booking Summary</h3>
        
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-gray-600" />
              <span className="font-cousine text-gray-700">Date</span>
            </div>
            <span className="font-medium text-black">{format(selectedDate, "MMMM d, yyyy")}</span>
          </div>
          
          <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-gray-600" />
              <span className="font-cousine text-gray-700">Time</span>
            </div>
            <span className="font-medium text-black">{getTimeSlotDisplay()}</span>
          </div>
          
          <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3">
              {getServiceIcon()}
              <span className="font-cousine text-gray-700">Service</span>
            </div>
            <span className="font-medium text-black">{bookingDetails.serviceType}</span>
          </div>
          
          <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center gap-3">
              <MapPin className="h-4 w-4 text-gray-600" />
              <span className="font-cousine text-gray-700">Address</span>
            </div>
            <span className="font-medium text-black text-right">{bookingDetails.address}</span>
          </div>
          
          {bookingDetails.frequency !== ServiceFrequency.OneTime && (
            <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center gap-3">
                <DollarSign className="h-4 w-4 text-gray-600" />
                <span className="font-cousine text-gray-700">Monthly Price</span>
              </div>
              <span className="font-medium text-black">${Math.round(calculateTotalWithAddOns())}</span>
            </div>
          )}
          
          {bookingDetails.addOns && bookingDetails.addOns.length > 0 && (
            <div className="pt-2">
              <span className="font-cousine text-gray-700 text-sm mb-2 block">Add-ons:</span>
              <div className="space-y-1 pl-4">
                {bookingDetails.addOns.map((addon, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="font-cousine text-gray-600">{addon.name}</span>
                    <span className="text-black">${Math.round(addon.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <Button
          className="w-full bg-black hover:bg-gray-800 text-white font-medium py-3"
          onClick={onConfirm}
          disabled={isLoading}
        >
          {isLoading ? "Confirming..." : "Confirm Booking"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BookingSummary;
